import { useCallback, useMemo } from 'react'
import log from 'loglevel'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { GetComplianceFrameworksRequest } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { CreateCustomFrameworkRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import {
  BulkCreateFrameworkRequirementsRequest,
  DeleteFrameworkRequirementsRequest,
  FrameworkRequirementGroup,
  FrameworkRequirementInput as FrameworkRequirementInputProto,
  SaveFrameworkRequirementGroupsRequest,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useFrameworks } from 'src/components/async/Framework/useFrameworks'
import {
  useControls,
  useInvalidateControlsCache,
} from 'src/components/async/model/control/useControls'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { useInvalidateComplianceFrameworksCache } from 'src/components/async/complianceframework/useComplianceFrameworks'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'
import { useAuthorizedGrpcClient } from 'src/adapter'
import {
  FrameworkRequirementInput,
  FrameworkWizardGroup,
} from './frameworks.types'

const mergeFrameworkGroupsForPersist = (
  wizardGroups: FrameworkWizardGroup[],
  requirements: FrameworkRequirementInput[],
): FrameworkWizardGroup[] => {
  const merged: FrameworkWizardGroup[] = []
  const seen = new Set<string>()

  for (const g of wizardGroups) {
    const name = g.name.trim()
    if (!name) continue
    const key = name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    const fromReqDesc = requirements
      .map((r) => ({
        cat: r.category.trim(),
        gd: (r.groupDescription ?? '').trim(),
      }))
      .find(
        (x) =>
          x.cat &&
          !x.cat.startsWith('trf-') &&
          x.cat.toLowerCase() === key &&
          x.gd,
      )?.gd
    merged.push({
      modelId: g.modelId,
      name,
      description: g.description.trim() || fromReqDesc || '',
    })
  }

  for (const r of requirements) {
    const cat = r.category.trim()
    if (!cat || cat.startsWith('trf-')) continue
    const key = cat.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    merged.push({
      name: cat,
      description: (r.groupDescription ?? '').trim(),
    })
  }

  return merged
}

const resolveCategoryToGroupModelId = (
  category: string,
  savedList: FrameworkRequirementGroup[],
): string => {
  const c = category.trim()
  if (!c) return ''
  if (c.startsWith('trf-')) return c
  const found = savedList.find(
    (g) => g.getName().trim().toLowerCase() === c.toLowerCase(),
  )
  return found?.getModelId() ?? ''
}

export const useSearchableFrameworks = (
  frameworks: ComplianceFramework[],
): SearchableData<ComplianceFramework>[] =>
  useMemo(
    () =>
      prepareSearchableData(
        frameworks,
        (framework: ComplianceFramework) => framework.getId(),
        (framework: ComplianceFramework) => [
          framework.getName(),
          framework.getDescription(),
        ],
      ),
    [frameworks],
  )

type FrameworkMetadataCounts = {
  requirementsByFrameworkId: Record<string, number>
  controlsByFrameworkId: Record<string, number>
  auditsByFrameworkId: Record<string, number>
  isLoading: boolean
}

export const useFrameworkMetadataCounts = (): FrameworkMetadataCounts => {
  const { data: frameworksData, isLoading: frameworksLoading } = useFrameworks()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    undefined,
    true,
    undefined,
    undefined,
    false,
    true,
  )
  const { data: auditsData, isLoading: auditsLoading } = useAudits()

  const requirementsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    frameworksData?.getItemsList().forEach((framework) => {
      const cfId = framework.getComplianceFrameworkId()
      if (cfId) counts[cfId] = (counts[cfId] ?? 0) + 1
    })
    return counts
  }, [frameworksData])

  const controlsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    controlsData?.getItemsList().forEach((control) => {
      control.getComplianceFrameworkIdsList().forEach((cfId) => {
        counts[cfId] = (counts[cfId] ?? 0) + 1
      })
    })
    return counts
  }, [controlsData])

  const auditsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    auditsData?.getItemsList().forEach((audit) => {
      audit.getComplianceFrameworksList().forEach((cf) => {
        const cfId = cf.getId()
        if (cfId) counts[cfId] = (counts[cfId] ?? 0) + 1
      })
    })
    return counts
  }, [auditsData])

  return {
    requirementsByFrameworkId,
    controlsByFrameworkId,
    auditsByFrameworkId,
    isLoading: frameworksLoading || controlsLoading || auditsLoading,
  }
}

export type CreateCustomFrameworkWithRequirementsArgs = {
  name: string
  description?: string
  requirements: FrameworkRequirementInput[]
  /** Base compliance framework model id when adding requirements to an existing framework. */
  appendComplianceFrameworkModelId?: string | null
  /** Wizard groups merged with requirement categories before SaveFrameworkRequirementGroups. */
  groups?: FrameworkWizardGroup[]
}

export const useCreateCustomFrameworkWithRequirements = (): ((
  args: CreateCustomFrameworkWithRequirementsArgs,
) => Promise<{ complianceFrameworkRecordId: string }>) => {
  const accountClient = useAuthorizedGrpcClient(AccountPromiseClient)
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const auditClient = useAuthorizedGrpcClient(AuditPromiseClient)
  const invalidateComplianceFrameworks =
    useInvalidateComplianceFrameworksCache()
  const invalidateControls = useInvalidateControlsCache()
  const revalidateByMethod = useGrpcRevalidateByMethod()

  return useCallback(
    async ({
      name,
      description,
      requirements,
      appendComplianceFrameworkModelId,
      groups = [],
    }): Promise<{ complianceFrameworkRecordId: string }> => {
      let complianceFrameworkModelId = appendComplianceFrameworkModelId ?? ''
      if (!complianceFrameworkModelId) {
        const response = await accountClient.createCustomFramework(
          new CreateCustomFrameworkRequest()
            .setName(name)
            .setDescription(description ?? ''),
        )
        complianceFrameworkModelId = response.getModelId()
      }

      const mergedGroups = mergeFrameworkGroupsForPersist(groups, requirements)
      let savedGroupRows: FrameworkRequirementGroup[] = []
      if (mergedGroups.length > 0) {
        const saveReq = new SaveFrameworkRequirementGroupsRequest()
          .setComplianceFrameworkModelId(complianceFrameworkModelId)
          .setGroupsList(
            mergedGroups.map((g) => {
              const row = new FrameworkRequirementGroup()
                .setName(g.name)
                .setDescription(g.description ?? '')
              if (g.modelId) {
                row.setModelId(g.modelId)
              }
              return row
            }),
          )
        const saveResp = await modelClient.saveFrameworkRequirementGroups(
          saveReq,
        )
        savedGroupRows = saveResp.getGroupsList()
      }

      if (requirements.length > 0) {
        const bulkRequest = new BulkCreateFrameworkRequirementsRequest()
          .setComplianceFrameworkModelId(complianceFrameworkModelId)
          .setRequirementsList(
            requirements.map((r) =>
              new FrameworkRequirementInputProto()
                .setName(r.name.trim())
                .setGroup(
                  resolveCategoryToGroupModelId(r.category, savedGroupRows),
                )
                .setDescription(r.description.trim())
                .setControlIdsList(r.controlIds),
            ),
          )
        await modelClient.bulkCreateFrameworkRequirements(bulkRequest)
      }

      await Promise.all([
        invalidateComplianceFrameworks(),
        invalidateControls(),
        revalidateByMethod(ModelPromiseClient.prototype.listFrameworks),
        revalidateByMethod(
          ModelPromiseClient.prototype.listFrameworkRequirementGroups,
        ),
      ])

      const refreshed = await auditClient.getComplianceFrameworks(
        new GetComplianceFrameworksRequest(),
      )
      const recordId = refreshed
        .getItemsList()
        .find((f) => f.getModelId() === complianceFrameworkModelId)
        ?.getId()

      if (!recordId) {
        log.error(
          'Created framework but could not resolve record id for navigation',
          { complianceFrameworkModelId },
        )
        throw new Error(
          'Framework was created but could not be loaded. Please refresh.',
        )
      }

      return { complianceFrameworkRecordId: recordId }
    },
    [
      accountClient,
      auditClient,
      invalidateComplianceFrameworks,
      invalidateControls,
      modelClient,
      revalidateByMethod,
    ],
  )
}

export const useDeleteFrameworkRequirements = (): ((
  requirementModelIds: string[],
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateComplianceFrameworks =
    useInvalidateComplianceFrameworksCache()
  const invalidateControls = useInvalidateControlsCache()
  const revalidateByMethod = useGrpcRevalidateByMethod()

  return useCallback(
    async (requirementModelIds: string[]): Promise<void> => {
      if (requirementModelIds.length === 0) return

      await modelClient.deleteFrameworkRequirements(
        new DeleteFrameworkRequirementsRequest().setRequirementModelIdsList(
          requirementModelIds,
        ),
      )

      await Promise.all([
        invalidateComplianceFrameworks(),
        invalidateControls(),
        revalidateByMethod(ModelPromiseClient.prototype.listFrameworks),
      ])
    },
    [
      invalidateComplianceFrameworks,
      invalidateControls,
      modelClient,
      revalidateByMethod,
    ],
  )
}
